import { app, Component, ojs, payload, v, state } from "modular-openscriptjs";
import { empty, clearSearchSuggestion, isMobile } from "../../../helpers";
import { api, dc } from "../../../declarations";
import SearchResultProductView from "../../Views/SearchResultProductView";
import QuickOrderCard from "./QuickOrderCard";
import { dom } from "../../../fotastart";

const h = app("h");
const router = app("router");
const broker = app("broker");

ojs(SearchResultProductView, QuickOrderCard);

export default class SubcategoryProductsResult extends Component {
    subcategoryName = state("Subcategory");
    parentCategoryName = state("");

    pageMap = {
        products: { next: 1, total: 0 },
    };

    // See SearchResults.js for why this exists: only the most recently
    // dispatched request's response is allowed to render.
    _searchSeq = 0;

    constructor() {
        super();
        this.resetMap();
        this.subcategoryName.listener(this);
        this.parentCategoryName.listener(this);
    }

    resetMap() {
        this.pageMap.products = { next: 1, total: 0 };

        // Also invalidate any request still in flight at the moment of the
        // reset, even before a replacement request goes out - so a slow
        // response from a subcategory we've since navigated away from can
        // never land in the wrong subcategory's list afterward.
        this._searchSeq++;
    }

    $$ojs = {
        routeChanged: () => this.resetMap(),
    };

    $$app = {
        has: {
            searchQuery: (ed, event) => {
                this.resetMap();
                this.getProducts("c-subcat-search-results");
            },
        },
    };

    render(...args) {
        return h.div(
            {
                class: "pt-3 pb-5 container mb-5 py-md-5",
            },
            h.div(
                { class: "mb-4 px-1" },
                h.h4(
                    { class: "fw-bold mb-1" },
                    "Search Results in ",
                    v(this.subcategoryName, (c) => c.value),
                ),
                v(this.parentCategoryName, (p) =>
                    p.value
                        ? h.p(
                              { class: "text-muted small mb-0" },
                              "in ",
                              h.span({ class: "fw-semibold" }, p.value),
                          )
                        : null,
                ),
            ),
            h.InfiniteScroll({
                child: h.div({
                    id: "c-subcat-search-results",
                    class: "d-flex gap-3 gap-md-4 flex-wrap justify-content-around justify-content-md-start pt-2 pb-3",
                }),
                callback: (entries, observer, marker) => {
                    entries.forEach((entry) => {
                        if (!entry.isIntersecting) return;
                        this.populateProducts(
                            "c-subcat-search-results",
                            marker,
                        );
                    });
                },
            }),
            ...args,
        );
    }

    $_mounted(componentId) {
        let c = component(componentId);
        if (!c) return;

        c.getProducts("c-subcat-search-results");
    }

    async getProducts(holderId, spinnerHolder = null) {
        const requestSeq = ++this._searchSeq;

        let page = this.pageMap.products.next;
        let query = dc.searchQuery.value ?? "";
        const categoryId = router.params.categoryId;

        let spinner = null;
        if (spinnerHolder) {
            spinner = h.Spinner({ parent: spinnerHolder, resetParent: true });
        }

        let qs = {
            page,
            interval: 10,
        };

        if (empty(query)) {
            return;
        }

        qs.query = query;

        let res = await api.get(
            `/subcategories/${categoryId}/search/query`,
            qs,
            {
                silent: true,
            },
        );

        // Stale response - a newer request has since been dispatched.
        if (requestSeq !== this._searchSeq) {
            spinner?.remove();
            return;
        }

        if (res) {
            let products = res.data.products.data;
            let currentPage = res.data.products.current_page;
            let lastPage = res.data.products.last_page;
            this.pageMap.products.total = res.data.products.total ?? 0;

            let subcategory = res.data.subcategory;
            let parentCategory = res.data.parentCategory;

            this.subcategoryName.value = subcategory?.name || "Subcategory";
            this.parentCategoryName.value = parentCategory?.name || "";

            const holder = dom.id(holderId);

            if (currentPage == 1) {
                dom.empty(holder);
            }

            for (let product of products) {
                if (!dom.get(`[feed-product-id="p-${product.id}"]`, holder)) {
                    h.SearchResultProductView(product, {
                        class: "flex-shrink-0 h-100",
                        style: isMobile() ? "width: 150px;" : "width: 280px;",
                        parent: holder,
                        $feed_product_id: `p-${product.id}`,
                        listeners: {
                            click: () => {
                                broker.send(
                                    app("$e").product.needs.display,
                                    payload({ product }),
                                );
                            },
                        },
                    });
                }
            }

            if (empty(products) && currentPage == 1) {
                this._showEmpty(
                    holderId,
                    `No results found in ${subcategory?.name || "this subcategory"}.`,
                );
            }

            if (!empty(products)) {
                this.pageMap.products.next = currentPage + 1;
            }

            if (currentPage === lastPage && !empty(products)) {
                if (!dom.get('[hook="quick-order-card-holder"]', holder)) {
                    h.div(
                        {
                            parent: holder,
                            class: "w-100 mt-4",
                            hook: "quick-order-card-holder",
                        },
                        h.QuickOrderCard(),
                    );
                }
            }
        }

        spinner?.remove();
        clearSearchSuggestion();
    }

    async populateProducts(holderId, marker) {
        this.getProducts(holderId, marker);
    }

    _showEmpty(holderId, message) {
        const holder = dom.id(holderId);
        if (!holder) return;

        h.div(
            {
                parent: holder,
                resetParent: true,
                class: "text-center py-5 w-100",
                id: "vp-no-subcat-search-results",
            },
            h.div(
                {
                    class: "rounded-circle mx-auto d-flex align-items-center justify-content-center mb-3",
                    style: "width: 64px; height: 64px; background: rgba(0,0,0,0.04);",
                },
                h.i({
                    class: "bi bi-search text-secondary",
                    style: "font-size: 1.5rem; opacity: 0.6;",
                }),
            ),
            h.h5({ class: "text-dark fw-bold mb-2" }, "No Results Found"),
            h.p({ class: "text-secondary small mb-0" }, message),
        );
    }
}

ojs(SubcategoryProductsResult);
